/**
 * Protocol run read shapes: the run records a protocol's readers see on the
 * Protocols list, the protocol page and the run record page (spec 2026-10-15
 * protocol runs layout, "API").
 *
 * Read only. Control of a run (step changes, pause, end) stays on
 * `/api/live-executions`, owner only; these come from the `/api/protocols`
 * read routes, for anyone who can read the protocol.
 *
 * Facts only: nothing here infers whether a session is running a run now.
 * `status` is the stored status and `lastActiveAt` the stored time.
 */
import { CAPTURE_METHOD_LIVE_TRANSCRIPT } from '../constants/capture';
/** The statuses a run can still change from: the "open" set. */
export const OPEN_RUN_STATUSES = [
    'active',
    'paused',
];
export function isOpenRunStatus(status) {
    return OPEN_RUN_STATUSES.includes(status);
}
/** The run's Live transcript note, told apart by its capture method: a
 * record of the whole run, not a capture on a step. */
export function isLiveTranscriptCapture(artifact) {
    return artifact.captureMethod === CAPTURE_METHOD_LIVE_TRANSCRIPT;
}
/**
 * A run's captures split as the run page, the run tool and the tally read
 * them: those stamped with a step, and the run-level ones (no step stamp).
 * The Live transcript is in neither. Order is kept.
 */
export function splitProtocolRunCaptures(captures) {
    const onSteps = [];
    const runLevel = [];
    for (const capture of captures) {
        if (isLiveTranscriptCapture(capture.artifact))
            continue;
        (capture.stepIndex === null ? runLevel : onSteps).push(capture);
    }
    return { onSteps, runLevel };
}
/** What a run capture is, for counting: from its artifact type and MIME type. */
export const PROTOCOL_RUN_CAPTURE_KINDS = ['photo', 'video', 'note', 'other'];
/** Fewer comparable runs than this and a "usually" figure is noise. */
export const TYPICAL_STEP_MIN_RUNS = 3;
/**
 * Seconds each DONE step took in one run: from the moment before it (the
 * latest close of any step earlier in time, else the run's start) to its own
 * close. Steps closed out of order still measure the gap the person spent
 * before closing them, which is what a reader of "took 3 min" expects. A
 * skipped step closes a gap but gets no time of its own.
 */
export function stepDurationsSeconds(startedAt, closes) {
    const ordered = [...closes].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
    const out = new Map();
    let previous = Date.parse(startedAt);
    for (const close of ordered) {
        const at = Date.parse(close.at);
        if (close.kind === 'done')
            out.set(close.stepId, Math.max(0, Math.round((at - previous) / 1000)));
        previous = Math.max(previous, at);
    }
    return out;
}
/** The median of a non-empty list, rounded to the second. */
export function medianSeconds(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return Math.round(sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2);
}
//# sourceMappingURL=protocol-runs.js.map